import { Map } from "immutable";
import * as React from "react";
import { useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import {
    allFlashReportsSummariesSelector,
    allManagersSelector,
    hasClientResearchEnabled,
    hasResearchAccess,
    isEditingPlanPortfolio,
    portfolioProductSummariesSelector,
    productUniverseSummariesSelector,
    selectProductSummariesBasedOnEditMode,
} from "../../../mainReducerMapSelectors";
import { FlashReportSummary } from "../../model/FlashReportSummary.model";
import { IStrategyUpdate } from "../../model/IStrategyUpdate.model";
import { ManagerResponse } from "../../model/manager/ManagerResponse.model";
import { ProductSummary } from "../../model/product/ProductSummary";
import { ErrorComponent } from "../base/Error.component";
import { getSuccessData, REQUEST_STATES, RequestState } from "../common/commonStates";
import {
    allFlashReportsSummariesRequestPair,
    allManagersRequestPair,
} from "../common/RequesterPairs";
import { FlashReports } from "../flash-report/FlashReports.component";
import { FormattedMessage } from "react-intl";
import { multipleApiRequesterWrapper } from "../common/MultipleApiRequesterWrapper";
import { IApplicationRootState } from "../../../applicationState";
import { ManagerStrategies } from "./ManagerStrategies.component";
import { LoadingSpinner } from "../icons/LoadingSpinner.component";
import {useParams} from "react-router";
import { getAllProducts, getPortfolioProductSummaries } from "../product/AllProductsActions";
import { hasConsultantLevelAccess } from "../../utils/sessionUtil";
import { AnyThunkDispatch } from "../../../commonTypes";

export interface IManagerPagePropsFromStore {
    editingPlanPortfolio: boolean;
    productSummaries: Map<number, ProductSummary> | null;
    productUniverseSummariesState: RequestState<Map<number, ProductSummary>>;
    portfolioProductSummariesState: RequestState<Map<number, ProductSummary>>;
    allManagers: Map<number, ManagerResponse>;
    allFlashReportSummaries: Map<number, FlashReportSummary>;
    hasClientResearchEnabled: boolean;
    hasResearchAccess: boolean;
}

export type IManagerComponentProps = IManagerPagePropsFromStore;

export const ManagerPageComponent: React.FC<IManagerComponentProps> = (props) => {
    const dispatch: AnyThunkDispatch = useDispatch();
    const {managerIdParam} = useParams();
    const managerId = Number.parseInt(managerIdParam!, 10);
    const manager = props.allManagers.get(managerId);

    useEffect(() => {
        if (props.productSummaries) {
            return;
        }

        const isConsultantEditing = hasConsultantLevelAccess() && props.editingPlanPortfolio;
        if (isConsultantEditing) {
            const shouldRequestUniverse = props.productUniverseSummariesState.kind === REQUEST_STATES.NOT_REQUESTED
                || props.productUniverseSummariesState.kind === REQUEST_STATES.REQUEST_FAILED;
            if (shouldRequestUniverse) {
                dispatch(getAllProducts);
            }
            return;
        }

        const shouldRequestPortfolio = props.portfolioProductSummariesState.kind === REQUEST_STATES.NOT_REQUESTED
            || props.portfolioProductSummariesState.kind === REQUEST_STATES.REQUEST_FAILED;
        if (shouldRequestPortfolio) {
            dispatch(getPortfolioProductSummaries);
        }
    }, [
        dispatch,
        props.editingPlanPortfolio,
        props.portfolioProductSummariesState.kind,
        props.productSummaries,
        props.productUniverseSummariesState.kind,
    ]);

    if (!props.productSummaries) {
        return <LoadingSpinner />;
    }

    if ((!props.hasClientResearchEnabled && !props.hasResearchAccess)
        || !managerIsAvailable(manager, props.editingPlanPortfolio)) {
        return <ErrorComponent />;
    }

    function managerIsAvailable(manager: ManagerResponse | undefined, editingPlanPortfolio: boolean): boolean {
        if (!manager) {
            return false;
        }
        return manager.inPortfolio || editingPlanPortfolio;
    }

    function getFlashReportSummaries() {
        return props.allFlashReportSummaries.valueSeq().toArray()
            .filter(isAttachedToManager);
    }

    function isAttachedToManager(strategyUpdate: IStrategyUpdate) {
        return strategyUpdate.managerIds.includes(managerId);
    }

    function renderManagerWebsiteButton() {
        const handleClick = () => {
            window.open(manager!.website, "_blank");
        };

        return (
            <div className="manager-website__button-container">
                <button className="manager-website__button" onClick={handleClick}>
                    Manager Website
                </button>
            </div>
        );
    }

    return (
        <div className="wealth-parent main-content manager-container new-common-styles">
            <div id="manager-info" className="manager-info">
                <div className="manager-header">
                    <h1 id="manager-name">
                        {manager!.name} -{" "}
                        <FormattedMessage id="manager.firm-summary" defaultMessage="Firm Summary" />
                    </h1>
                    {manager!.website && renderManagerWebsiteButton()}
                </div>
                <ManagerStrategies {...{ managerId, productSummaries: props.productSummaries }} />
                <FlashReports flashReportSummaries={getFlashReportSummaries()} />
            </div>
        </div>
    );
};

export const mapStateToProps = (state: IApplicationRootState): IManagerPagePropsFromStore => {
    return {
        editingPlanPortfolio: isEditingPlanPortfolio(state),
        productSummaries: getSuccessData(selectProductSummariesBasedOnEditMode(state))!,
        productUniverseSummariesState: productUniverseSummariesSelector(state)!,
        portfolioProductSummariesState: portfolioProductSummariesSelector(state)!,
        allManagers: getSuccessData(allManagersSelector(state))!,
        allFlashReportSummaries: getSuccessData(allFlashReportsSummariesSelector(state))!,
        hasClientResearchEnabled: hasClientResearchEnabled(state),
        hasResearchAccess: hasResearchAccess(state),
    };
};

const connectedComponent = connect<IManagerPagePropsFromStore, null>(
    mapStateToProps,
    null
)(ManagerPageComponent);

export default multipleApiRequesterWrapper(
    connectedComponent,
    [
        allManagersRequestPair,
        allFlashReportsSummariesRequestPair,
    ],
);